import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Card, Row, Col, Spin, DatePicker } from 'antd';
import {
  UserOutlined,
  SoundOutlined,
  CrownOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { Line, Pie } from '@ant-design/plots';
import dayjs from 'dayjs';
import {
  getBasicStats,
  getDailyAudioStats,
  getAudioStatusStats,
  getAudioSourceStats,
} from '../../graphql/queries';
import './Dashboard.css';

const { RangePicker } = DatePicker;

// Default: last 7 days
const getDefaultDateRange = () => {
  const endDate = dayjs();
  const startDate = dayjs().subtract(7, 'day');
  return [startDate, endDate];
};

const Dashboard = () => {
  const [audioStatsDateRange, setAudioStatsDateRange] = useState(getDefaultDateRange());
  const [sourceStatsDateRange, setSourceStatsDateRange] = useState(getDefaultDateRange());

  const { loading: basicStatsLoading, data: basicStatsData } = useQuery(getBasicStats);

  const defaultRange = getDefaultDateRange();
  const defaultStartDate = defaultRange[0].format('YYYY-MM-DD');
  const defaultEndDate = defaultRange[1].format('YYYY-MM-DD');

  const {
    loading: dailyAudioStatsLoading,
    data: dailyAudioStatsData,
    refetch: refetchDailyAudioStats
  } = useQuery(getDailyAudioStats, {
    variables: { startDate: defaultStartDate, endDate: defaultEndDate },
    notifyOnNetworkStatusChange: true
  });

  const {
    loading: audioStatusStatsLoading,
    data: audioStatusStatsData,
    refetch: refetchAudioStatusStats
  } = useQuery(getAudioStatusStats, {
    variables: { startDate: defaultStartDate, endDate: defaultEndDate },
    notifyOnNetworkStatusChange: true
  });

  const {
    loading: audioSourceStatsLoading,
    data: audioSourceStatsData,
    refetch: refetchAudioSourceStats
  } = useQuery(getAudioSourceStats, {
    variables: { startDate: defaultStartDate, endDate: defaultEndDate },
    notifyOnNetworkStatusChange: true
  });

  const handleAudioStatsDateChange = async (dates) => {
    setAudioStatsDateRange(dates);
    const params = dates && dates[0] && dates[1]
      ? { startDate: dates[0].format('YYYY-MM-DD'), endDate: dates[1].format('YYYY-MM-DD') }
      : { startDate: null, endDate: null };
    await Promise.all([refetchDailyAudioStats(params), refetchAudioStatusStats(params)]);
  };

  const handleSourceStatsDateChange = async (dates) => {
    setSourceStatsDateRange(dates);
    const params = dates && dates[0] && dates[1]
      ? { startDate: dates[0].format('YYYY-MM-DD'), endDate: dates[1].format('YYYY-MM-DD') }
      : { startDate: null, endDate: null };
    await refetchAudioSourceStats(params);
  };

  if (basicStatsLoading) return <Spin size="large" className="dashboard-loading" />;

  const basicStats = basicStatsData?.getBasicStats || {};
  const dailyAudioStats = dailyAudioStatsData?.getDailyAudioStats || [];
  const audioStatusStats = audioStatusStatsData?.getAudioStatusStats || {};
  const audioSourceStats = audioSourceStatsData?.getAudioSourceStats || {};

  const statusPieConfig = {
    data: [
      { type: 'Succeeded', value: audioStatusStats?.succeeded || 0 },
      { type: 'Failed', value: audioStatusStats?.failed || 0 },
      { type: 'Running', value: audioStatusStats?.running || 0 },
      { type: 'In Queue', value: audioStatusStats?.in_queue || 0 },
      { type: 'Pending', value: audioStatusStats?.pending || 0 },
    ].filter(d => d.value > 0),
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    innerRadius: 0.6,
    label: { type: 'inner', content: '{percentage}', style: { fill: '#fff', fontSize: 12 } },
    legend: { position: 'bottom' },
    color: ['#17ad37', '#f53939', '#2152ff', '#7928ca', '#8392ab'],
    statistic: {
      title: { content: 'Total', style: { color: '#344767', fontSize: 14 } },
      content: { style: { color: '#344767', fontSize: 24, fontWeight: 700 } }
    }
  };

  const sourcePieConfig = {
    data: [
      { type: 'FAL_AI', value: audioSourceStats?.fal_ai || 0 },
      { type: 'KIE_AI', value: audioSourceStats?.kie_ai || 0 },
    ].filter(d => d.value > 0),
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    innerRadius: 0.6,
    label: { type: 'inner', content: '{percentage}', style: { fill: '#fff', fontSize: 12 } },
    legend: { position: 'bottom' },
    color: ['#2152ff', '#17ad37'],
    statistic: {
      title: { content: 'Sources', style: { color: '#344767', fontSize: 14 } },
      content: { style: { color: '#344767', fontSize: 24, fontWeight: 700 } }
    }
  };

  const lineConfig = {
    data: dailyAudioStats,
    xField: 'date',
    yField: 'count',
    smooth: true,
    point: { size: 4, shape: 'circle' },
    color: '#7928CA',
    areaStyle: { fill: 'l(270) 0:rgba(121,40,202,0.1) 1:rgba(121,40,202,0.4)' },
    xAxis: { label: { style: { fill: '#8392ab', fontSize: 10 } } },
    yAxis: { label: { style: { fill: '#8392ab', fontSize: 10 } }, grid: { line: { style: { stroke: '#e9ecef' } } } },
  };

  const statsCards = [
    { title: 'Total Users', value: basicStats.totalUsers || 0, icon: <UserOutlined />, gradient: 'primary' },
    { title: 'Total Audios', value: basicStats.totalAudios || 0, icon: <SoundOutlined />, gradient: 'info' },
    { title: 'Active Subs', value: basicStats.activeSubscriptions || 0, icon: <CrownOutlined />, gradient: 'success' },
    {
      title: 'Last Audio',
      value: basicStats.lastAudioDate
        ? new Date(basicStats.lastAudioDate).toLocaleDateString('tr-TR')
        : '-',
      icon: <ClockCircleOutlined />,
      gradient: 'warning'
    }
  ];

  return (
    <div className="dashboard-container">
      {/* Page Header */}
      <div className="dashboard-header">
        <p className="dashboard-breadcrumb">Pages / Dashboard</p>
        <h1 className="dashboard-title">Dashboard</h1>
      </div>

      {/* Stats Cards */}
      <Row gutter={[24, 24]} className="stats-row">
        {statsCards.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <div className="stat-card">
              <div className="stat-card-content">
                <span className="stat-card-label">{stat.title}</span>
                <span className="stat-card-value">{stat.value}</span>
              </div>
              <div className={`stat-card-icon ${stat.gradient}`}>
                {stat.icon}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Charts Row 1 */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={8}>
          <Card
            className="chart-card"
            title="Audio Status"
            extra={<RangePicker onChange={handleAudioStatsDateChange} value={audioStatsDateRange} size="small" />}
          >
            <div className="chart-container">
              {audioStatusStatsLoading ? <Spin /> : <Pie {...statusPieConfig} />}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            className="chart-card"
            title="Daily Audio Generation"
            extra={<RangePicker onChange={handleAudioStatsDateChange} value={audioStatsDateRange} size="small" />}
          >
            <div className="chart-container">
              {dailyAudioStatsLoading ? <Spin /> : <Line {...lineConfig} />}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            className="chart-card"
            title="Generation Source"
            extra={<RangePicker onChange={handleSourceStatsDateChange} value={sourceStatsDateRange} size="small" />}
          >
            <div className="chart-container">
              {audioSourceStatsLoading ? <Spin /> : <Pie {...sourcePieConfig} />}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
